import { useCallback, useEffect, useRef, useState } from 'react'
import { dispatch } from 'store'
import { generateTrialTts_API } from 'api/tts'
import { waitDataFromSocket } from 'services/socket-factory'
import { handleApiError } from 'store/utils'
import { ErrorKeys } from 'consts/errorKeys'
import { playAudioAndCatchAbortError } from 'utils'
import { ApiError } from 'utils/errors'
import { ITtsTrialSocketMessage, TPlayState } from './types'
import { useAlertPopupsContext } from 'context/AlertPopups'
import { translate } from 'utils/translations'
import { T } from 'consts/translationKeys'
import { TTS_LANGUAGE_MISMATCH_ERROR_IMG } from 'consts/images'

// Identifies a generated trial by its inputs — same text + voice (catalog
// speaker or cloned-voice audio) means the audio is unchanged, so we can
// replay it instead of regenerating.
const getTrialKey = (text: string, voiceKey: number | string) =>
  `${voiceKey}::${text}`

// Drives the speech "play" preview: POST the text → await the audio for the
// returned tts_hash over the socket → autoplay it once.
export const useTrialAudio = () => {
  const [playState, setPlayState] = useState<TPlayState>('idle')
  // Playback progress, 0..1, for the play control's progress ring.
  const [progress, setProgress] = useState(0)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  // The (text + speaker) key the currently-loaded audio was generated for.
  // Lets us skip a fresh trial request when nothing changed.
  const loadedKeyRef = useRef<string | null>(null)
  // Tracks mount status so an in-flight trial that resolves after the section
  // unmounts can't start detached, uncontrollable playback.
  const isMountedRef = useRef(true)

  const { setAlertPopupsData } = useAlertPopupsContext()

  const showAlertPopup = useCallback(() => {
    setAlertPopupsData({
      warningPopup: {
        title: translate(T.GENERATION_FAILED),
        description: translate(T.TTS_GENERATION_ERROR_DESCRIPTION),
        imageSrc: TTS_LANGUAGE_MISMATCH_ERROR_IMG,
        buttons: {
          confirm: {
            label: translate(T.GOT_IT_LABEL)
          }
        }
      }
    })
  }, [setAlertPopupsData])

  const getAudio = useCallback(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio()
      audioRef.current.onended = () => {
        setPlayState('idle')
        setProgress(0)
      }
      audioRef.current.ontimeupdate = () => {
        const audio = audioRef.current
        if (audio?.duration) {
          setProgress(audio.currentTime / audio.duration)
        }
      }
    }
    return audioRef.current
  }, [])

  const stop = useCallback(() => {
    const audio = audioRef.current
    if (audio) {
      audio.pause()
      audio.currentTime = 0
    }
    setPlayState('idle')
    setProgress(0)
  }, [])

  const play = useCallback(
    async (text: string, speakerId: number, refAudio?: string) => {
      const key = getTrialKey(text, refAudio ?? speakerId)

      // Same text + voice as the last generated trial — replay, don't regenerate.
      if (key === loadedKeyRef.current && audioRef.current?.src) {
        const audio = getAudio()
        audio.currentTime = 0
        setProgress(0)
        setPlayState('playing')
        playAudioAndCatchAbortError(audio)
        return
      }

      setPlayState('loading')
      try {
        // A cloned voice replaces the catalog speaker — the backend expects
        // either `refAudio` or `id`, not both.
        const { data } = await generateTrialTts_API(
          refAudio ? { text, refAudio } : { id: speakerId, text }
        )
        const response = await waitDataFromSocket<ITtsTrialSocketMessage>(
          'tts_trial',
          res => res.tts_hash === data.tts_hash
        )

        // The section may have unmounted while we awaited the socket — don't
        // start playback on a detached audio element or touch state if so.
        if (!isMountedRef.current) {
          return
        }

        if (response.state === 'error') {
          dispatch(handleApiError(ErrorKeys.CUSTOM_API_ERROR, response.message))
          setPlayState('idle')
          return
        }

        if (response.data) {
          const audio = getAudio()
          audio.src = response.data.url
          loadedKeyRef.current = key
          setProgress(0)
          setPlayState('playing')
          playAudioAndCatchAbortError(audio)
        }
      } catch (error) {
        const apiError = error as ApiError
        if (apiError.cause?.code === 'TTS_LANGUAGE_MISMATCH_ERROR') {
          showAlertPopup()
        } else {
          dispatch(handleApiError(ErrorKeys.CUSTOM_API_ERROR, apiError.message))
        }
        setPlayState('idle')
      }
    },
    [getAudio, showAlertPopup]
  )

  // On unmount: mark unmounted (so an in-flight trial that resolves later won't
  // start playback) and fully tear down the audio — pause, detach the source,
  // and drop the handlers so nothing keeps playing or fires after teardown.
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      const audio = audioRef.current
      if (audio) {
        audio.pause()
        audio.src = ''
        audio.onended = null
        audio.ontimeupdate = null
      }
    }
  }, [])

  return { playState, progress, play, stop }
}
